import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { Building2, ArrowLeft, Save, Users, Calendar, AlertCircle, Power, Plus, ChevronRight, ChevronDown, Shield, User as UserIcon, Trash2 } from 'lucide-react';
import { Tenant, TenantStatus, User, UserRole, UserStatus } from '../../types/admin';
import { GET_TENANT, UPDATE_TENANT, DEACTIVATE_TENANT } from '../../graphql/admin';
import { useAuth } from '../../contexts/AuthContext';
import { usePlatformAdminUsers, usePlatformAdminCreateUser, usePlatformAdminUpdateUser, usePlatformAdminDeleteUser } from '../../hooks/useAdmin';
import TenantStatusBadge from '../../components/admin/TenantStatusBadge';
import UserStatusBadge from '../../components/admin/UserStatusBadge';
import { ConfirmModal } from '../../components/Modal';
import { TenantFeatureManager } from '../../components/admin/TenantFeatureManager';

/**
 * TenantDetailPage - Display and edit tenant information
 * Requirements: 10.3, 10.4, 12.3
 */
const TenantDetailPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { loading: authLoading, error: authError } = useAuth();
  
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Edit form state
  const [editName, setEditName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  // User management state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [createFormData, setCreateFormData] = useState({ email: '', name: '', role: 'USER' as 'ADMIN' | 'USER' });
  const [createValidationError, setCreateValidationError] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState({ email: '', name: '', role: '' as string, status: '' as string });
  const [userSuccessMessage, setUserSuccessMessage] = useState<string | null>(null);
  const [userErrorMessage, setUserErrorMessage] = useState<string | null>(null);

  // Check if this is the owner tenant (allows PLATFORM_ADMIN role)
  const isOwnerTenant = tenant?.name === 'Docari';
  const { data: users, loading: usersLoading, error: usersError, nextToken: usersNextToken, fetchUsers, refetch: refetchUsers } = usePlatformAdminUsers(tenantId || null);
  const { createUser, loading: createUserLoading, error: createUserError } = usePlatformAdminCreateUser();
  const { updateUser, loading: updateUserLoading, error: updateUserError } = usePlatformAdminUpdateUser();
  const { deleteUser, loading: deleteUserLoading } = usePlatformAdminDeleteUser();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null); // userId to delete

  // Handle authorization errors - redirect to unauthorized page
  useEffect(() => {
    if (!authLoading && authError) {
      // Check if it's an authorization error
      if (authError.toLowerCase().includes('unauthorized') || 
          authError.toLowerCase().includes('forbidden') ||
          authError.toLowerCase().includes('not found')) {
        navigate('/unauthorized', { replace: true });
      }
    }
  }, [authLoading, authError, navigate]);

  const fetchTenant = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      setError(null);

      // Check if we're in preview mode
      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        // Mock data for preview mode
        const mockTenant: Tenant = {
          tenantId,
          name: 'Acme Corporation',
          status: TenantStatus.ACTIVE,
          userCount: 15,
          createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date().toISOString()
        };
        setTenant(mockTenant);
        setEditName(mockTenant.name);
        return;
      }

      const client = generateClient();
      const response = await client.graphql({
        query: GET_TENANT,
        variables: { tenantId }
      }) as any;

      if (response.data?.getTenant) {
        setTenant(response.data.getTenant);
        setEditName(response.data.getTenant.name);
      } else {
        setError('Tenant not found');
      }
    } catch (err: any) {
      console.error('Failed to fetch tenant:', err);
      setError(err.message || 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  // Fetch users when tenant is loaded
  useEffect(() => {
    if (tenant && tenantId) {
      fetchUsers();
    }
  }, [tenant, tenantId, fetchUsers]);

  const handleCreateUser = async () => {
    setCreateValidationError(null);
    if (!createFormData.email.trim() || !createFormData.name.trim()) {
      setCreateValidationError('Email and name are required.');
      return;
    }
    if (!tenantId) return;

    const result = await createUser(tenantId, {
      email: createFormData.email.trim(),
      name: createFormData.name.trim(),
      role: createFormData.role as UserRole,
    });

    if (result) {
      setShowCreateForm(false);
      setCreateFormData({ email: '', name: '', role: 'USER' });
      setCreateValidationError(null);
      setUserSuccessMessage('User created successfully');
      refetchUsers();
      setTimeout(() => setUserSuccessMessage(null), 3000);
    } else {
      setUserErrorMessage(createUserError || 'Failed to create user');
      setTimeout(() => setUserErrorMessage(null), 5000);
    }
  };

  const handleUpdateUser = async (userId: string) => {
    if (!tenantId) return;

    const result = await updateUser(tenantId, userId, {
      email: editFormData.email.trim(),
      name: editFormData.name.trim(),
      status: editFormData.status as UserStatus,
    });

    if (result) {
      setExpandedUserId(null);
      setUserSuccessMessage('User updated successfully');
      refetchUsers();
      setTimeout(() => setUserSuccessMessage(null), 3000);
    } else {
      setUserErrorMessage(updateUserError || 'Failed to update user');
      setTimeout(() => setUserErrorMessage(null), 5000);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!tenantId) return;
    const success = await deleteUser(tenantId, userId);
    if (success) {
      setExpandedUserId(null);
      setShowDeleteConfirm(null);
      setUserSuccessMessage('User deleted successfully');
      refetchUsers();
      setTimeout(() => setUserSuccessMessage(null), 3000);
    } else {
      setUserErrorMessage('Failed to delete user');
      setTimeout(() => setUserErrorMessage(null), 5000);
    }
  };

  const handleExpandUser = (user: User) => {
    if (expandedUserId === user.userId) {
      setExpandedUserId(null);
    } else {
      setExpandedUserId(user.userId);
      setEditFormData({
        email: user.email,
        name: user.name,
        role: user.role,
        status: user.status,
      });
    }
  };

  const getRoleBadgeStyles = (role: string) => {
    return role === 'ADMIN'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-gray-100 text-gray-800';
  };

  const handleSave = async () => {
    if (!tenant || !editName.trim()) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        // Mock update for preview
        setTenant({ ...tenant, name: editName.trim(), updatedAt: new Date().toISOString() });
        setIsEditing(false);
        setSuccessMessage('Tenant updated successfully');
        return;
      }

      const client = generateClient();
      const response = await client.graphql({
        query: UPDATE_TENANT,
        variables: { 
          tenantId: tenant.tenantId, 
          input: { name: editName.trim() } 
        }
      }) as any;

      if (response.data?.updateTenant) {
        setTenant(response.data.updateTenant);
        setIsEditing(false);
        setSuccessMessage('Tenant updated successfully');
      }
    } catch (err: any) {
      console.error('Failed to update tenant:', err);
      setError(err.message || 'Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!tenant) return;

    try {
      setSaving(true);
      setError(null);

      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        // Mock deactivation for preview
        setTenant({ ...tenant, status: TenantStatus.INACTIVE, updatedAt: new Date().toISOString() });
        setShowDeactivateModal(false);
        setSuccessMessage('Tenant deactivated successfully');
        return;
      }

      const client = generateClient();
      const response = await client.graphql({
        query: DEACTIVATE_TENANT,
        variables: { tenantId: tenant.tenantId }
      }) as any;

      if (response.data?.deactivateTenant) {
        setTenant(response.data.deactivateTenant);
        setSuccessMessage('Tenant deactivated successfully');
      }
    } catch (err: any) {
      console.error('Failed to deactivate tenant:', err);
      setError(err.message || 'Failed to deactivate tenant');
    } finally {
      setSaving(false);
      setShowDeactivateModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !tenant) {
    return (
      <div className="space-y-6">
        <Link
          to="/platform-admin/tenants"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tenants
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!tenant) return null;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/platform-admin/tenants"
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Tenants
      </Link>

      {/* Success message */}
      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-emerald-700">
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          {error}
        </div>
      )}

      {/* Tenant header */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
              <p className="text-sm text-gray-500">ID: {tenant.tenantId}</p>
            </div>
          </div>
          <TenantStatusBadge status={tenant.status} />
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-gray-500 text-sm mb-1">
              <Users className="w-4 h-4 mr-2" />
              Total Users
            </div>
            <p className="text-2xl font-bold text-gray-900">{usersLoading ? '—' : users.length}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-gray-500 text-sm mb-1">
              <Calendar className="w-4 h-4 mr-2" />
              Created
            </div>
            <p className="text-lg font-medium text-gray-900">
              {new Date(tenant.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-gray-500 text-sm mb-1">
              <Calendar className="w-4 h-4 mr-2" />
              Last Updated
            </div>
            <p className="text-lg font-medium text-gray-900">
              {new Date(tenant.updatedAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Edit form */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Tenant Details</h2>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="tenantName" className="block text-sm font-medium text-gray-700 mb-1">
              Tenant Name
            </label>
            {isEditing ? (
              <input
                id="tenantName"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{tenant.name}</p>
            )}
          </div>

          <div className="flex items-center space-x-3 pt-4">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || !editName.trim()}
                  className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditName(tenant.name);
                  }}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Feature Management */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <TenantFeatureManager 
          tenant={tenant} 
          onUpdate={(updatedTenant) => setTenant(updatedTenant)}
        />
      </div>

      {/* Users section */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Users
          </h2>
          <button
            onClick={() => { setShowCreateForm(true); setCreateValidationError(null); }}
            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add User
          </button>
        </div>

        {/* User success/error messages */}
        {userSuccessMessage && (
          <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">
            {userSuccessMessage}
          </div>
        )}
        {userErrorMessage && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {userErrorMessage}
          </div>
        )}

        {/* Create user form */}
        {showCreateForm && (
          <div className="mb-4 border border-blue-200 rounded-lg p-4 bg-blue-50/50">
            <h3 className="text-sm font-medium text-gray-900 mb-3">New User</h3>
            {createValidationError && (
              <div className="mb-3 text-sm text-red-600">{createValidationError}</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label htmlFor="create-email" className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  id="create-email"
                  type="email"
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label htmlFor="create-name" className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input
                  id="create-name"
                  type="text"
                  value={createFormData.name}
                  onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Full Name"
                />
              </div>
              <div>
                <label htmlFor="create-role" className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                <select
                  id="create-role"
                  value={createFormData.role}
                  onChange={(e) => setCreateFormData({ ...createFormData, role: e.target.value as 'ADMIN' | 'USER' })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                  {isOwnerTenant && <option value="PLATFORM_ADMIN">Platform Admin</option>}
                </select>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCreateUser}
                disabled={createUserLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {createUserLoading ? 'Creating...' : 'Submit'}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setCreateFormData({ email: '', name: '', role: 'USER' }); setCreateValidationError(null); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Users loading */}
        {usersLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : usersError ? (
          <div className="text-sm text-red-600 py-4">{usersError}</div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <UserIcon className="mx-auto h-10 w-10 text-gray-300 mb-2" />
            <p className="text-sm">No users have been added to this tenant yet.</p>
            <p className="text-xs mt-1">Click "Add User" to create the first user.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {users.map((user) => (
                <div key={user.userId}>
                  <button
                    onClick={() => handleExpandUser(user)}
                    className="w-full flex items-center justify-between py-3 px-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <UserIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeStyles(user.role)}`}>
                        <Shield className="w-3 h-3 mr-1" />
                        {user.role}
                      </span>
                      <UserStatusBadge status={user.status} />
                      {expandedUserId === user.userId ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* Inline edit form */}
                  {expandedUserId === user.userId && (
                    <div className="px-2 pb-3">
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label htmlFor={`edit-name-${user.userId}`} className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                            <input
                              id={`edit-name-${user.userId}`}
                              type="text"
                              value={editFormData.name}
                              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label htmlFor={`edit-email-${user.userId}`} className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                            <input
                              id={`edit-email-${user.userId}`}
                              type="email"
                              value={editFormData.email}
                              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label htmlFor={`edit-role-${user.userId}`} className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                            <select
                              id={`edit-role-${user.userId}`}
                              value={editFormData.role}
                              onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="USER">User</option>
                              <option value="ADMIN">Admin</option>
                              {isOwnerTenant && <option value="PLATFORM_ADMIN">Platform Admin</option>}
                            </select>
                          </div>
                          <div>
                            <label htmlFor={`edit-status-${user.userId}`} className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                            <select
                              id={`edit-status-${user.userId}`}
                              value={editFormData.status}
                              onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="ACTIVE">Active</option>
                              <option value="INACTIVE">Inactive</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleUpdateUser(user.userId)}
                            disabled={updateUserLoading}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            {updateUserLoading ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button
                            onClick={() => setExpandedUserId(null)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(user.userId)}
                            disabled={deleteUserLoading}
                            className="ml-auto px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {usersNextToken && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => fetchUsers(usersNextToken)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Danger zone */}
      {tenant.status === TenantStatus.ACTIVE && (
        <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Danger Zone</h2>
          <p className="text-sm text-gray-600 mb-4">
            Deactivating a tenant will prevent all users in this tenant from logging in.
          </p>
          <button
            onClick={() => setShowDeactivateModal(true)}
            className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            <Power className="w-4 h-4 mr-2" />
            Deactivate Tenant
          </button>
        </div>
      )}

      {/* Deactivate confirmation modal */}
      {showDeactivateModal && (
        <ConfirmModal
          title="Deactivate Tenant"
          message={`Are you sure you want to deactivate "${tenant.name}"? All users in this tenant will be unable to log in.`}
          confirmLabel="Deactivate"
          variant="danger"
          onConfirm={handleDeactivate}
          onCancel={() => setShowDeactivateModal(false)}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title="Delete User"
          message="Are you sure you want to delete this user? This action cannot be undone and will remove the user from both the system and Cognito."
          confirmLabel={deleteUserLoading ? 'Deleting...' : 'Delete'}
          variant="danger"
          onConfirm={() => handleDeleteUser(showDeleteConfirm)}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

export default TenantDetailPage;
